import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import MenuItem from '../MenuItem';
import { forgeClassHelper } from '../utils/classes';

const classes = forgeClassHelper('menu');

// Return the desired aria-role for the option's type.
export function getRole(type) {
  switch (type) {
    case 'option':
    case undefined: // Since option is the default, undefined passed into this represents an option.
      return 'option';
    case 'title':
      return 'heading';
    default:
      return '';
  }
}

/** Selection menu for navigation and displaying additional actions.  */
const Menu = React.forwardRef((props, ref) => {
  const { label, children, hideLabel, className, borderRadius, size } = props;
  const { isOpen, getToggleButtonProps, getLabelProps, getMenuProps, highlightedIndex, getItemProps } = useSelect({
    items: children,
  });

  const Trigger = useMemo(() => {
    if (React.isValidElement(props.trigger)) {
      // use supportedProps to decide what props are to be consumed
      const triggerProps = (props.trigger.type.supportedProps || []).reduce(
        (triggerProps, supportedProp) => {
          if (supportedProp === 'size') {
            triggerProps.size = size;
          }
          // Set the active prop when the menu is open. This styles the trigger as active.
          else if (supportedProp === 'active') {
            triggerProps.active = isOpen;
          } else if (supportedProp === 'borderRadius') {
            triggerProps.borderRadius = borderRadius;
          }
          return triggerProps;
        },
        { ...getToggleButtonProps() }
      );
      return React.cloneElement(props.trigger, triggerProps);
    }
  }, [props.trigger, size, isOpen, borderRadius, getToggleButtonProps]);

  return (
    <div ref={ref}>
      {/* eslint-disable jsx-a11y/label-has-for */}
      {/* Disabling eslint on next line to avoid jsx-a11y/label-has-for because getLabelProps handles that */}
      {/* eslint-disable-next-line */}
      <label className={hideLabel ? 'fe_u_visually-hidden' : ''} {...getLabelProps()}>
        {label}
      </label>
      {Trigger}
      <ul
        {...getMenuProps()}
        {...classes({ element: 'ul', extra: !isOpen ? `${className} fe_u_visually-hidden` : className })}
      >
        {isOpen &&
          React.Children.map(children, (item, index) => {
            const { children, className, ...rest } = item.props;
            return (
              <li
                {...classes({
                  element: 'li',
                  states: {
                    active:
                      (item.props.type === 'option' || item.props.type === undefined) && highlightedIndex === index,
                  },
                  // Assigning className prop to li as outlined in the MenuItem docs
                  extra: className,
                })}
                key={`${index}`}
                {...getItemProps({
                  item,
                  index,
                  role: getRole(item.props.type),
                  disabled: item.props.type !== 'option' && item.props.type !== undefined, // Since option is default, undefined === "option" too.
                })}
              >
                <MenuItem {...rest}>{children}</MenuItem>
              </li>
            );
          })}
      </ul>
    </div>
  );
});

Menu.propTypes = {
  /** Input label. Required for accessibility reasons, but can be hidden via `hideLabel` */
  label: PropTypes.string.isRequired,

  /** Button/trigger that toggles opening/closing the menu */
  trigger: PropTypes.any.isRequired,

  /** Specify which side(s) of the trigger's button should have a border radius */
  borderRadius: PropTypes.oneOf(['all', 'left', 'right', 'top', 'bottom', 'none']),

  /** CSS class applied to the Menu's root element */
  className: PropTypes.string,

  /** Array of items to display in the menu, should only be menuItem */
  children: PropTypes.any,

  /** Set to true to hide the label  */
  hideLabel: PropTypes.bool,

  /** Specify the Menu trigger's size */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

Menu.defaultProps = {
  borderRadius: 'all',
  size: 'medium',
};

export default Menu;
